import { RootState } from "../app/store";
import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import type { PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { Car } from "../models/car";
import { CarService } from "../services/car-service";
import { FILTER_KEYS, FilterableValues, Filters } from "../models/filter";

export interface CarsState {
  status: "idle" | "loading" | "succeeded" | "failed";
  syncStatus: "idle" | "syncing" | "succeeded" | "failed";
  items: Car[];
  filterableValues: FilterableValues;
  filters: Filters;
  filtersOpened: boolean;
  error?: SerializedError;
  syncError?: SerializedError;
  initDone: boolean;
}

const getFiltersFromUrl = (): Filters => {
  const searchParams = new URLSearchParams(window.location.search);
  const filters: Filters = {};

  FILTER_KEYS.forEach((key) => {
    const value = searchParams.get(key);

    if (value !== null && value !== "") {
      filters[key] = value;
    }
  });

  return filters;
};

const initialState: CarsState = {
  status: "idle",
  syncStatus: "idle",
  items: [],
  filterableValues: undefined,
  filters: getFiltersFromUrl(),
  filtersOpened: false,
  error: undefined,
  syncError: undefined,
  initDone: false,
};

export const fetchCars = createAsyncThunk(
  "cars/fetchCars",
  async (_arg, { getState }) => {
    const state = getState() as RootState;
    const currentFilters = state.cars.filters;
    return await CarService.getCars(currentFilters);
  },
);

export const syncCars = createAsyncThunk(
  "cars/syncCars",
  async (_arg, { getState }) => {
    return await CarService.syncCars().then(async () => {
      const state = getState() as RootState;
      const currentFilters = state.cars.filters;
      return await CarService.getCars(currentFilters);
    });
  },
);

export const carsSlice = createSlice({
  name: "cars",
  initialState,
  reducers: {
    deleteCar: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((car) => car._id !== action.payload);
    },
    addCar: (state, action: PayloadAction<Car>) => {
      state.items.push(action.payload);
    },
    updateCar: (state, action: PayloadAction<Car>) => {
      const index = state.items.findIndex(
        (car) => car._id === action.payload._id,
      );
      state.items[index] = action.payload;
    },
    openFilters: (state) => {
      state.filtersOpened = true;
    },
    closeFilters: (state) => {
      state.filtersOpened = false;
    },
    updateFilters: (state, action: PayloadAction<Filters>) => {
      state.filters = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCars.pending, (state, action) => {
        if (!current(state).initDone) {
          state.status = "loading";
        }
      })
      .addCase(fetchCars.fulfilled, (state, action) => {
        const { items, filterableValues } = action.payload;
        state.status = "succeeded";
        state.items = items;
        state.filterableValues = filterableValues;
        state.initDone = true;
      })
      .addCase(fetchCars.rejected, (state, action) => {
        if (!current(state).initDone) {
          state.status = "failed";
          state.error = action.error;
        }
      })
      .addCase(syncCars.pending, (state) => {
        state.syncStatus = "syncing";
      })
      .addCase(syncCars.fulfilled, (state, action) => {
        const { items, filterableValues } = action.payload;
        state.items = items;
        state.filterableValues = filterableValues;
        state.syncStatus = "succeeded";
      })
      .addCase(syncCars.rejected, (state, action) => {
        state.syncStatus = "failed";
        state.syncError = action.error;
      });
  },
});

export const selectAllCars = (state: RootState) => state.cars.items;
export const selectCar = (id: string) => (state: RootState) =>
  state.cars.items.find((car) => car._id === id);
export const selectFilterableValues = (state: RootState) =>
  state.cars.filterableValues;
export const selectFilters = (state: RootState) => state.cars.filters;
export const selectFiltersOpened = (state: RootState) =>
  state.cars.filtersOpened;

export const selectCarsStatus = (state: RootState) => state.cars.status;
export const selectCarsError = (state: RootState) => state.cars.error;

export const selectSyncStatus = (state: RootState) => state.cars.syncStatus;
export const selectSyncError = (state: RootState) => state.cars.syncError;

export const {
  addCar,
  updateCar,
  deleteCar,
  openFilters,
  closeFilters,
  updateFilters,
} = carsSlice.actions;

export default carsSlice.reducer;
